<template>
  <div class="group-admin" v-loading="booting">
    <el-card shadow="never" class="panel-card">
      <div class="toolbar">
        <el-button icon="el-icon-refresh" @click="loadGroupList">刷新</el-button>
      </div>
      <el-table :data="groupRows" border class="data-table">
        <el-table-column prop="groupName" label="租户名称" min-width="180" />
        <el-table-column prop="memberCount" label="成员数" width="120" align="center" />
        <el-table-column prop="taskCount" label="监测任务数" width="140" align="center" />
        <el-table-column label="编辑" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" type="primary" plain @click="openEditDrawer(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 编辑：仅租户成员维护（名称不可改） -->
    <el-drawer
      title="租户成员"
      :visible.sync="editDrawerVisible"
      size="860px"
      append-to-body
      custom-class="group-edit-drawer"
      @closed="onEditDrawerClosed"
    >
      <div class="edit-drawer-body" v-loading="editLoading">
        <section class="edit-section">
          <div class="section-header">
            <h3 class="section-title">成员列表</h3>
            <div class="toolbar">
              <el-button type="primary" icon="el-icon-plus" size="small" @click="openMemberDialog()">新增成员</el-button>
              <el-button icon="el-icon-refresh" size="small" @click="loadMembers">刷新</el-button>
            </div>
          </div>
          <el-table :data="members" border class="data-table">
            <el-table-column prop="id" label="ID" width="70" />
            <el-table-column prop="username" label="用户名" width="120" />
            <el-table-column prop="phone" label="手机号" width="130" />
            <el-table-column prop="displayName" label="展示名称" width="120" />
            <el-table-column label="角色" width="100">
              <template slot-scope="{ row }">{{ roleLabel(row.roleCode) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="row.enabled === false ? 'info' : 'success'" size="mini">
                  {{ row.enabled === false ? '已禁用' : '启用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" label="创建时间" width="170" />
            <el-table-column label="操作" fixed="right" width="180" align="center">
              <template slot-scope="{ row }">
                <el-button size="mini" @click="openMemberDialog(row)">编辑</el-button>
                <el-button
                  v-if="canDisable(row)"
                  size="mini"
                  type="danger"
                  plain
                  @click="confirmDisable(row)"
                >移出</el-button>
              </template>
            </el-table-column>
          </el-table>
        </section>
      </div>
    </el-drawer>

    <el-dialog
      :title="memberDialogTitle"
      :visible.sync="memberDialogVisible"
      width="480px"
      append-to-body
      @closed="resetMemberForm"
    >
      <el-form ref="memberFormRef" :model="memberForm" :rules="memberRules" label-width="96px">
        <el-form-item v-if="!editingMemberId" label="用户名" prop="username">
          <el-input v-model="memberForm.username" autocomplete="off" />
        </el-form-item>
        <el-form-item v-else label="用户名">
          <el-input :value="memberForm.username" disabled />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="memberForm.phone" autocomplete="off" maxlength="11" />
        </el-form-item>
        <el-form-item label="展示名称" prop="displayName">
          <el-input v-model="memberForm.displayName" autocomplete="off" />
        </el-form-item>
        <p v-if="!editingMemberId" class="form-hint">新增成员角色固定为「成员」，不可指定为租户管理员。</p>
      </el-form>
      <div slot="footer">
        <el-button @click="memberDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="memberSubmitting" @click="submitMember">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  createGroupMember,
  disableGroupMember,
  getGroupInfo,
  getGroupUsage,
  listGroupMembers,
  updateGroupMember
} from './api'

export default {
  name: 'GroupAdmin',
  props: {
    authUser: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      booting: false,
      groupInfo: {},
      groupRows: [],
      editDrawerVisible: false,
      editLoading: false,
      members: [],
      memberDialogVisible: false,
      memberSubmitting: false,
      editingMemberId: null,
      memberForm: {
        username: '',
        phone: '',
        displayName: ''
      },
      memberRules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
        ],
        displayName: [{ required: true, message: '请输入展示名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    memberDialogTitle() {
      return this.editingMemberId ? '编辑成员' : '新增成员'
    }
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    async bootstrap() {
      this.booting = true
      try {
        await this.loadGroupList()
      } finally {
        this.booting = false
      }
    },
    // 用租户信息 + 用量拼装列表行（当前租户管理员仅管理本租户，通常 1 行）
    async loadGroupList() {
      try {
        const [info, usage] = await Promise.all([getGroupInfo(), getGroupUsage()])
        this.groupInfo = info || {}
        const memberCount =
          (usage && usage.memberCount != null)
            ? usage.memberCount
            : (this.groupInfo.memberCount != null ? this.groupInfo.memberCount : 0)
        const taskCount = (usage && usage.taskCount != null) ? usage.taskCount : 0
        if (!this.groupInfo.id && !(this.groupInfo.groupName || (usage && usage.groupName))) {
          this.groupRows = []
          return
        }
        this.groupRows = [
          {
            id: this.groupInfo.id,
            groupName: this.groupInfo.groupName || (usage && usage.groupName) || '—',
            memberCount,
            taskCount
          }
        ]
      } catch (error) {
        this.groupRows = []
        this.$message.error((error && error.message) || '加载租户列表失败')
      }
    },
    async openEditDrawer() {
      this.editDrawerVisible = true
      this.editLoading = true
      try {
        await this.loadMembers()
      } finally {
        this.editLoading = false
      }
    },
    onEditDrawerClosed() {
      this.members = []
    },
    roleLabel(code) {
      return code === 'GROUP_LEADER' ? '租户管理员' : '成员'
    },
    canDisable(row) {
      if (!row || row.enabled === false) return false
      if (this.authUser && row.id === this.authUser.id) return false
      if (row.roleCode === 'GROUP_LEADER') return false
      return true
    },
    async loadMembers() {
      try {
        this.members = (await listGroupMembers()) || []
      } catch (error) {
        this.$message.error((error && error.message) || '加载成员失败')
      }
    },
    openMemberDialog(row) {
      if (row) {
        this.editingMemberId = row.id
        this.memberForm = {
          username: row.username || '',
          phone: row.phone || '',
          displayName: row.displayName || ''
        }
      } else {
        this.editingMemberId = null
        this.resetMemberForm()
      }
      this.memberDialogVisible = true
    },
    resetMemberForm() {
      this.editingMemberId = null
      this.memberForm = { username: '', phone: '', displayName: '' }
      this.memberSubmitting = false
      if (this.$refs.memberFormRef) this.$refs.memberFormRef.clearValidate()
    },
    submitMember() {
      this.$refs.memberFormRef.validate(async valid => {
        if (!valid) return
        this.memberSubmitting = true
        try {
          if (this.editingMemberId) {
            await updateGroupMember(this.editingMemberId, {
              phone: this.memberForm.phone.trim(),
              displayName: this.memberForm.displayName.trim()
            })
            this.$message.success('成员已更新')
          } else {
            await createGroupMember({
              username: this.memberForm.username.trim(),
              phone: this.memberForm.phone.trim(),
              displayName: this.memberForm.displayName.trim()
            })
            this.$message.success('成员已创建')
          }
          this.memberDialogVisible = false
          await this.loadMembers()
          await this.loadGroupList()
        } catch (error) {
          this.$message.error((error && error.message) || '保存失败')
        } finally {
          this.memberSubmitting = false
        }
      })
    },
    async confirmDisable(row) {
      try {
        await this.$confirm(`确认移出成员「${row.displayName || row.username}」？移出后将无法登录。`, '移出成员', {
          type: 'warning'
        })
        await disableGroupMember(row.id)
        this.$message.success('已移出（账号已禁用）')
        await this.loadMembers()
        await this.loadGroupList()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error((error && error.message) || '移出失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.group-admin {
  min-height: 320px;
}

.panel-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.form-hint {
  color: #6b7280;
  font-size: 13px;
}

.data-table {
  width: 100%;
}

.edit-drawer-body {
  padding: 0 20px 24px;
}

.edit-section {
  margin-bottom: 28px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.section-header .toolbar {
  margin-bottom: 0;
}

.section-title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.section-header .section-title {
  margin-bottom: 0;
}
</style>
